import { AppLogger } from '@/logger';
import type { PaymentMethodDto } from '@/castapi';
import type { IActionParams } from '@/store/modules/index';
import { getErrorMessage, getPaymentMethodsApi } from '@/castapi/helpers';

const logger = new AppLogger('payment methods state');

interface IPaymentMethodsState {
  paymentMethods: PaymentMethodDto[];
  paymentMethodsLoadError?: string;
  paymentMethodsLoading: boolean;
}

const initialState = (): IPaymentMethodsState => ({
  paymentMethods: [],
  paymentMethodsLoadError: undefined,
  paymentMethodsLoading: false,
});

type ActionParams = IActionParams<IPaymentMethodsState>;

export default {
  namespaced: true,
  state: initialState,
  mutations: {
    RESET_STATE(state: IPaymentMethodsState): void {
      const initState = initialState();
      Object.keys(initState).forEach((key: string) => {
        state[key] = initState[key];
      });
    },
    PAYMENT_METHODS_LOADING(state: IPaymentMethodsState): void {
      state.paymentMethods = [];
      state.paymentMethodsLoading = true;
      state.paymentMethodsLoadError = undefined;
    },
    PAYMENT_METHODS_LOADED(state: IPaymentMethodsState, paymentMethods: PaymentMethodDto[]): void {
      state.paymentMethodsLoading = false;
      state.paymentMethods = [...paymentMethods];
    },
    PAYMENT_METHODS_LOAD_ERROR(state: IPaymentMethodsState, paymentMethodsLoadError: Error): void {
      state.paymentMethodsLoading = false;
      state.paymentMethodsLoadError = getErrorMessage(paymentMethodsLoadError);
    },
    PAYMENT_METHOD_CHANGING(state: IPaymentMethodsState): void {
      state.paymentMethodsLoadError = undefined;
      state.paymentMethodsLoading = true;
    },
    PAYMENT_METHOD_CHANGED(state: IPaymentMethodsState): void {
      state.paymentMethodsLoading = false;
    },
  },
  actions: {
    resetState({ commit }: ActionParams): void {
      // unused
      commit('RESET_STATE');
    },

    async loadPaymentMethods({ commit, rootGetters }: ActionParams): Promise<void> {
      const accessToken = rootGetters['login/accessToken'];
      try {
        const organizationId = rootGetters['organizations/organizationId'];
        commit('PAYMENT_METHODS_LOADING');
        const response =
          await getPaymentMethodsApi(accessToken).paymentMethodsControllerFindAllByOrganization(organizationId);
        commit('PAYMENT_METHODS_LOADED', response.data);
      } catch (error) {
        commit('PAYMENT_METHODS_LOAD_ERROR', error);
        logger.captureStoreError('loadPaymentMethods', error);
      }
    },

    async deletePaymentMethod({ rootGetters, commit }: ActionParams, paymentMethodId: number): Promise<void> {
      const accessToken = rootGetters['login/accessToken'];
      try {
        commit('PAYMENT_METHOD_CHANGING');
        await getPaymentMethodsApi(accessToken).paymentMethodsControllerDeletePaymentMethod(paymentMethodId);
        commit('PAYMENT_METHOD_CHANGED');
      } catch (error) {
        commit('PAYMENT_METHODS_LOAD_ERROR', error);
        logger.captureStoreError('deletePaymentMethod', error);
      }
    },

    async cancelPaymentMethodDeletion({ rootGetters, commit }: ActionParams, paymentMethodId: number): Promise<void> {
      const accessToken = rootGetters['login/accessToken'];
      try {
        commit('PAYMENT_METHOD_CHANGING');
        await getPaymentMethodsApi(accessToken).paymentMethodsControllerCancelPaymentMethodDeletion(paymentMethodId);
        commit('PAYMENT_METHOD_CHANGED');
      } catch (error) {
        commit('PAYMENT_METHODS_LOAD_ERROR', error);
        logger.captureStoreError('cancelPaymentMethodDeletion', error);
      }
    },

    // async dropDeletionDate({ rootGetters, commit }: ActionParams, paymentMethodId: number): Promise<void> {
    //   const accessToken = rootGetters['login/accessToken'];
    //   try {
    //     commit('PAYMENT_METHOD_CHANGING');
    //     await getPaymentMethodsApi(accessToken).paymentMethodsControllerDropDeletionDate(paymentMethodId);
    //     commit('PAYMENT_METHOD_CHANGED');
    //   } catch (error) {
    //     commit('PAYMENT_METHODS_LOAD_ERROR', error);
    //     logger.captureStoreError('dropDeletionDate', error);
    //   }
    // },
  },
  getters: {
    paymentMethods: (state: IPaymentMethodsState): PaymentMethodDto[] => state.paymentMethods,
    paymentMethodsLoading: (state: IPaymentMethodsState): boolean => state.paymentMethodsLoading,
    paymentMethodsLoadError: (state: IPaymentMethodsState): undefined | string => state.paymentMethodsLoadError,
  },
};
